import { getLanguageDef, getLocale } from './locale';

// Currency symbol mapping (ISO 4217)
const CURRENCY_SYMBOLS: Record<string, string> = {
  TRY: '₺', USD: '$', EUR: '€', GBP: '£', JPY: '¥',
  CNY: '¥', KRW: '₩', INR: '₹', RUB: '₽', BRL: 'R$',
  AUD: 'A$', CAD: 'C$', CHF: 'CHF', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei',
  ILS: '₪', AED: 'د.إ', SAR: '﷼', ZAR: 'R', MXN: 'MX$',
  THB: '฿', MYR: 'RM', SGD: 'S$', HKD: 'HK$', TWD: 'NT$',
  PHP: '₱', IDR: 'Rp', VND: '₫', EGP: 'E£', NGN: '₦', PKR: '₨',
};

export function getCurrencySymbol(currencyCode: string = 'TRY'): string {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
}

export function formatCurrency(
  value: number | string | undefined | null,
  currencyCode: string = 'TRY'
): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!num && num !== 0) return placeCurrencySymbol('0', getCurrencySymbol(currencyCode));
  return placeCurrencySymbol(new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num), getCurrencySymbol(currencyCode));
}

// Where a currency symbol goes in a locale, as Intl writes it ("€1.00" / "1,00 €")
const intlPositions = new Map<string, 'before' | 'after'>();
function intlCurrencyPosition(locale: string): 'before' | 'after' {
  let position = intlPositions.get(locale);
  if (!position) {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).formatToParts(1);
    position = parts.findIndex((p) => p.type === 'currency') < parts.findIndex((p) => p.type === 'integer') ? 'before' : 'after';
    intlPositions.set(locale, position);
  }
  return position;
}

/** "1.234 ₺" or "$1,234" by the UI language; a letter code ("CHF") always goes after */
export function placeCurrencySymbol(amount: string, symbol: string, separator = ' '): string {
  const def = getLanguageDef();
  if ((def.currencyPosition ?? intlCurrencyPosition(def.locale)) === 'before' && !/^[A-Za-z]+$/.test(symbol)) {
    return amount.startsWith('-') ? `-${symbol}${amount.slice(1)}` : `${symbol}${amount}`;
  }
  return `${amount}${separator}${symbol}`;
}

export function currencyAdornment(currencyCode: string = 'TRY'): string {
  return getCurrencySymbol(currencyCode);
}

export function isMixedCurrency(currencies: string[]): boolean {
  const unique = Array.from(new Set(currencies.filter(Boolean)));
  return unique.length > 1;
}
